<template>
  <v-card>
    <slot name="above-title" />
    <v-card-title> Add Counter Party </v-card-title>

    <v-card-text>
      <v-form
        ref="form"
        v-model="valid"
        role="form"
        @keyup.enter.native="addCounterparty()"
      >
        <v-radio-group
          v-model="accountType"
          row
          label="Counter Party is a"
        >
          <v-radio
            :value="SINGLE_USER.id"
            label="Individual"
          />
          <v-radio
            :value="STANDARD_BUSINESS.id"
            label="Business"
          />
        </v-radio-group>
        <v-text-field
          ref="email"
          v-model="email"
          :disabled="saving"
          :rules="[validateRequired, ...emailRules]"
          label="Email"
          type="email"
          autofocus
          required
          @blur="emailDirty = true"
        />
        <v-text-field
          ref="name"
          v-model="name"
          :disabled="saving"
          :rules="[validateRequired, ...nameRules]"
          label="Name"
          required
          @blur="nameDirty = true"
        />
        <v-text-field
          v-if="isStandardBusiness"
          ref="businessName"
          v-model="businessName"
          :disabled="saving"
          :rules="[validateRequired, ...businessNameRules]"
          label="Business name"
          type="businessName"
          required
          @blur="businessNameDirty = true"
        />
        <v-alert
          text
          type="info"
        >
          New counter parties will receive an email invitation when you send
          them a document to sign.
        </v-alert>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="saving"
        color="primary"
        text
        large
        @click.stop="cancel"
      >
        Cancel
      </v-btn>
      <v-btn
        :disabled="!valid || !emailValid || !nameValid || saving"
        :loading="saving"
        color="primary"
        large
        @click.stop="addCounterparty()"
      >
        Add Counter Party
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { validateRequired } from '@/utils/validationRules'
import {
  isStandardBusinessAccount,
  SINGLE_USER,
  STANDARD_BUSINESS
} from '@/dataAccess/accountTypes'

const createErrorMessage =
  ({ name, email }) =>
    (error) => {
      if (error.isAxiosError) {
        switch (error.response.status) {
        case 409:
          return `User '${email}' is already a counterparty`
        case 400:
          return `'${email}' is not a valid email`
        }
      }
      return `Error adding counterparty "${name}" (${email})`
    }

export default {
  name: 'AddUserForm',
  data: function () {
    return {
      accountType: SINGLE_USER.id,
      SINGLE_USER,
      STANDARD_BUSINESS,
      saving: false,
      valid: false,
      businessName: '',
      businessNameDirty: false,
      email: '',
      emailDirty: false,
      name: '',
      nameDirty: false,
      emailValid: true
    }
  },
  computed: {
    isStandardBusiness() {
      return isStandardBusinessAccount(this.accountType)
    },
    emailRules() {
      var rules = []
      if (this.emailDirty && !this.emailValid) {
        rules.push("That's not a valid email address.")
      } else {
        rules.push(true)
      }
      return rules
    },
    businessNameRules() {
      var rules = []
      if (this.businessNameDirty && !this.businessNameValid) {
        rules.push('Please enter a valid business name.')
      } else {
        rules.push(true)
      }
      return rules
    },
    businessNameValid() {
      return !this.isStandardBusiness || this.businessName.length > 0
    },
    nameRules() {
      var rules = []
      if (this.nameDirty && !this.nameValid) {
        rules.push('Please enter first, middle (if known), and last names.')
      } else {
        rules.push(true)
      }
      return rules
    },
    nameValid() {
      return /\w\w+\s\S\w+/.test(this.name)
    }
  },
  watch: {
    email: function (email) {
      this.emailValid = this.$refs.email.$refs.input.validity.valid
    }
  },
  methods: {
    validateRequired,
    cancel() {
      this.reset()
      this.$emit('cancel')
    },
    reset() {
      Object.assign(this.$data, this.$options.data())
    },
    addCounterparty() {
      Object.entries(this.$data).forEach(([key, value]) => {
        if (key.includes('Dirty')) {
          this[key] = true
        }
      })

      if (this.$refs.form.validate()) {
        this.saving = true
        const { name, email, accountType, businessName } = this
        const label = `"${name}" (${email})`
        this.$handlingError(
          createErrorMessage({ name, email }),
          async () => {
            await this.$api.addCounterparty({
              name,
              email,
              accountType,
              businessName
            })
            this.$showSuccessbar(`Counterparty ${label} added`)
            this.reset()
            this.$emit('counterpartyAdded')
          },
          {
            final: () => {
              this.saving = false
            }
          }
        )
      }
    }
  }
}
</script>
